import { useLingui } from '@lingui/react/macro';
import { type ReactNode, useMemo } from 'react';
import { useHasAccessToken } from '@/auth/api';
import { useMe } from '../../profile/api';
import docsImg from './docs.svg';
import infoImg from './info.svg';
import phoneImg from './phone.svg';

export type IdentificationStep = {
  key: string;
  features: { title: string; enabled: boolean }[];
  status: 'waiting' | 'completed' | 'failed' | 'processing';
  needAction: boolean;
  title: ReactNode;
  formTitle: ReactNode;
  subtitle: ReactNode;
  icon: string;
};

export const useIdentificationSteps = () => {
  const { t } = useLingui();
  const me = useMe();
  const hasAccessToken = useHasAccessToken();

  return useMemo(() => {
    const features = {
      deposit_iqd: t`Deposit IQD`,
      withdraw_iqd: t`Withdraw IQD`,
      deposit_crypto: t`Deposit Crypto`,
      withdraw_crypto: t`Withdraw Crypto`,
    };
    const feature = (k: keyof typeof features, enabled: boolean) => ({
      title: features[k],
      enabled,
    });

    const steps: IdentificationStep[] = [];

    // 01 - Registration
    const phoneStepStatus: IdentificationStep['status'] =
      hasAccessToken && me.data?.phone_number ? 'completed' : 'waiting';
    const phoneStep: IdentificationStep = {
      key: 'phone',
      features: [
        feature('deposit_iqd', false),
        feature('withdraw_iqd', false),
        feature('deposit_crypto', false),
        feature('withdraw_crypto', false),
      ],
      icon: phoneImg,
      title: t`Registration`,
      formTitle: t`Phone Verification`,
      subtitle: t`Complete registration and bind phone number`,
      status: phoneStepStatus,
      needAction: phoneStepStatus === 'waiting',
    };
    steps.push(phoneStep);

    // 02 - Basic Verification
    const infoStepStatus: IdentificationStep['status'] =
      phoneStep.status !== 'completed' || !me.data?.kyc_info_status
        ? 'waiting'
        : me.data.kyc_info_status === 'Approving'
          ? 'processing'
          : me.data.kyc_info_status === 'Verified'
            ? 'completed'
            : 'failed';
    const infoStep: IdentificationStep = {
      key: 'info',
      features: [
        feature('deposit_iqd', true),
        feature('withdraw_iqd', true),
        feature('deposit_crypto', true),
        feature('withdraw_crypto', false),
      ],
      icon: infoImg,
      title: t`Basic Verification`,
      formTitle: t`Personal Information`,
      subtitle: t`Submit your information`,
      status: infoStepStatus,
      needAction:
        (infoStepStatus === 'failed' || infoStepStatus === 'waiting') &&
        phoneStepStatus === 'completed',
    };
    steps.push(infoStep);

    // 03 - Basic Verification
    const docsStepStatus =
      infoStep.status === 'waiting' || !me.data?.kyc_docs_status
        ? 'waiting'
        : me.data.kyc_docs_status === 'Approving'
          ? 'processing'
          : me.data.kyc_docs_status === 'Verified'
            ? 'completed'
            : 'failed';
    const docsStep: IdentificationStep = {
      key: 'docs',
      features: [
        feature('deposit_iqd', true),
        feature('withdraw_iqd', true),
        feature('deposit_crypto', true),
        feature('withdraw_crypto', true),
      ],
      icon: docsImg,
      title: t`VIP Verification`,
      formTitle: t`Identify Information`,
      subtitle: t`Submit ID and identify with video`,
      status: docsStepStatus,
      needAction:
        (docsStepStatus === 'failed' || docsStepStatus === 'waiting') &&
        infoStepStatus === 'completed',
    };
    steps.push(docsStep);

    return steps;
  }, [t, me.data, hasAccessToken]);
};
